import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

import { ArrowLeft, ArrowRight } from "lucide-react";

import Pagination from "../../../Components/Pagination";
import { useScrollChainRef } from "../../../Hooks/useScrollChain";
import KpiCompareMissingDataModal, {
  getSelectedAccountsWithoutKpiData,
} from "../../../intelligence-briefing-library/components/hawkeye/KpiCompareMissingDataModal";
import KpiCompareSelectionStrip from "../../../intelligence-briefing-library/components/hawkeye/KpiCompareSelectionStrip";
import SpiderCompareModalContent from "../../../intelligence-briefing-library/components/hawkeye/SpiderCompareModalContent";
import {
  Dialog,
  DialogContent,
} from "../../../intelligence-briefing-library/components/ui/dialog";
import {
  buildAccountMatrixData,
  fetchHawkeyeDigestState,
  getAvailableCategories,
  getDefaultMetricsConfiguration,
  getOrderedCategories,
  loadMetricsConfigurationFromApi,
  type AccountMatrixData,
  type MetricsConfiguration,
} from "../../../intelligence-briefing-library/hooks/hawkeyeMetricsCore";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchXFactorAccountBriefingsViews,
  setXFactorOrg,
} from "../../../Store/Reducers/XFactorViewsSlice";
import type { XFactorPortfolioAccount } from "../../../Types/XFactorViewsRendering";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../../Utils/Helper/LocalStorage";
import {
  hasAlwaysOnRowRevealCompleted,
  markAlwaysOnRowRevealCompleted,
} from "../../AlwaysOnChannel/Dashboard/alwaysOnRowReveal";
import BubbleMatrix from "../components/BubbleMatrix";
import { ACCENT, Modal } from "../components/Chrome";
import ExecutiveBrief, {
  ExecutiveBriefSectionNav,
} from "../components/ExecutiveBrief";
import FullBriefingModal from "../components/FullBriefingModal";
import SectionDetailModal from "../components/SectionDetailModal";
import SituationCard from "../components/SituationCard";
import { formatDate } from "../lib/derive";
import { isPendingDigestId, pendingDigestId } from "../lib/watchListMapper";

export { isPendingDigestId, pendingDigestId };

const MIN_COMPARE_ACCOUNTS = 2;
const COMPARE_COLORS = ["#7c3aed", "#2563eb", "#0d9488", "#d97706"];
const DEFAULT_BRIEFING_PAGE_SIZE = 20;
const BRIEFING_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50, 100];

const areAccountSelectionsEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((accountId, index) => accountId === right[index]);

function resolveLoginTenantId(): string {
  const loggedIn = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER) as {
    tenantId?: string;
    tenant_id?: string;
    user?: { tenant_id?: string };
  } | null;
  return (
    loggedIn?.tenantId || loggedIn?.tenant_id || loggedIn?.user?.tenant_id || ""
  );
}

export type TempPublicDataAccount = {
  id: string;
  name?: string | null;
};

type TempPublicDataPanelProps = {
  /**
   * EB accounts for this view. Briefings are loaded only for these ids via
   * POST /account-briefings (never account_scope=all).
   */
  accounts?: TempPublicDataAccount[];
  /** @deprecated Prefer `accounts`. Kept for older callers that only pass ids. */
  accountIds?: string[];
  /** Shared Always On highlight / compare selection (EB customer ids). */
  highlightedAccountIds?: string[];
  onHighlightedAccountIdsChange?: (accountIds: string[]) => void;
  onSaveAsWatchlist?: (accountIds: string[]) => void;
  /** Briefing panel bounds for the floating compare strip. */
  selectionPanelRef?: RefObject<HTMLElement | null>;
  /**
   * When true (default), accounts with a briefing sit first and generating
   * rows go to the bottom — same as Account Health's KPI toggle.
   */
  showUnavailableBriefingData?: boolean;
  onBriefingDataCountsChange?: (counts: {
    available: number;
    unavailable: number;
    loading: boolean;
  }) => void;
  onAccountsWithoutBriefingDataChange?: (accountIds: string[]) => void;
};

/**
 * Temp Public Data — one matrix row per supplied EB account; live payloads
 * from POST /account-briefings. Selection + Compare mirrors Account Health.
 */
const TempPublicDataPanel: React.FC<TempPublicDataPanelProps> = ({
  accounts: accountsProp,
  accountIds = [],
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  onSaveAsWatchlist,
  selectionPanelRef,
  showUnavailableBriefingData = true,
  onBriefingDataCountsChange,
  onAccountsWithoutBriefingDataChange,
}) => {
  const dispatch = useAppDispatch();
  const { orgId, freshnessDays, accountBriefings, payloads } = useAppSelector(
    s => s.xfactorViews
  );
  const loginOrgId = useMemo(() => resolveLoginTenantId(), []);

  const ebAccounts = useMemo((): TempPublicDataAccount[] => {
    if (accountsProp?.length) {
      return accountsProp
        .map(a => ({
          id: String(a.id || "").trim(),
          name: a.name || null,
        }))
        .filter(a => a.id);
    }
    return accountIds
      .map(id => ({ id: String(id || "").trim(), name: null }))
      .filter(a => a.id);
  }, [accountsProp, accountIds]);

  const accountIdsKey = useMemo(
    () => ebAccounts.map(a => a.id).join(","),
    [ebAccounts]
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_BRIEFING_PAGE_SIZE);
  /** Dot-by-dot reveal only the first time this Always On visit paints. */
  const [hasRevealedOnce, setHasRevealedOnce] = useState(() =>
    hasAlwaysOnRowRevealCompleted()
  );

  const markRevealDone = useCallback(() => {
    markAlwaysOnRowRevealCompleted();
    setHasRevealedOnce(true);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [accountIdsKey]);

  useEffect(() => {
    if (page !== 1) markRevealDone();
  }, [page, markRevealDone]);

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  useEffect(() => {
    if (!loginOrgId) return;
    if (orgId !== loginOrgId) dispatch(setXFactorOrg(loginOrgId));
  }, [dispatch, loginOrgId, orgId]);

  // Load briefings for every EB account once; pagination is display-only.
  useEffect(() => {
    if (!loginOrgId || !ebAccounts.length) return;

    dispatch(
      fetchXFactorAccountBriefingsViews({
        account_ids: ebAccounts.map(a => a.id),
        freshnessDays,
        accountNames: Object.fromEntries(ebAccounts.map(a => [a.id, a.name])),
      })
    );
  }, [dispatch, loginOrgId, freshnessDays, accountIdsKey, ebAccounts.length]);

  const allLoadedAccounts = accountBriefings.accounts;

  const pendingBriefingAccountIds = useMemo(() => {
    const ids: string[] = [];
    allLoadedAccounts.forEach(account => {
      const pending =
        account.has_payload === false || isPendingDigestId(account.digest_id);
      if (!pending) return;
      const id = String(account.account_id || "").trim();
      if (id) ids.push(id);
    });
    return ids;
  }, [allLoadedAccounts]);

  useEffect(() => {
    const loading = Boolean(
      accountBriefings.loading && !allLoadedAccounts.length
    );
    if (loading) {
      onBriefingDataCountsChange?.({
        available: 0,
        unavailable: 0,
        loading: true,
      });
      onAccountsWithoutBriefingDataChange?.([]);
      return;
    }

    onBriefingDataCountsChange?.({
      available: allLoadedAccounts.length - pendingBriefingAccountIds.length,
      unavailable: pendingBriefingAccountIds.length,
      loading: false,
    });
    onAccountsWithoutBriefingDataChange?.(pendingBriefingAccountIds);
  }, [
    accountBriefings.loading,
    allLoadedAccounts,
    pendingBriefingAccountIds,
    onBriefingDataCountsChange,
    onAccountsWithoutBriefingDataChange,
  ]);

  const [section, setSection] = useState<{
    digestId: string;
    sectionId: string;
  } | null>(null);
  const [cardAccount, setCardAccount] =
    useState<XFactorPortfolioAccount | null>(null);
  const [cardView, setCardView] = useState<"situation" | "brief">("situation");
  const [briefingAccount, setBriefingAccount] =
    useState<XFactorPortfolioAccount | null>(null);

  const [selectedCompareAccountIds, setSelectedCompareAccountIds] = useState<
    string[]
  >([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [isMissingKpiDataModalOpen, setIsMissingKpiDataModalOpen] =
    useState(false);
  const [compareSelectionMessage, setCompareSelectionMessage] = useState<
    string | null
  >(null);
  const [kpiMatrixData, setKpiMatrixData] = useState<AccountMatrixData[]>([]);
  const [kpiCategories, setKpiCategories] = useState<string[]>([]);
  const kpiConfigRef = useRef<MetricsConfiguration | null>(null);
  const kpiLoadedForKeyRef = useRef<string>("");
  const matrixScrollRef = useRef<HTMLDivElement | null>(null);
  const setMatrixScrollRef = useScrollChainRef(matrixScrollRef);

  useEffect(() => {
    setSelectedCompareAccountIds(prevIds =>
      areAccountSelectionsEqual(prevIds, highlightedAccountIds)
        ? prevIds
        : highlightedAccountIds
    );
  }, [highlightedAccountIds]);

  // Selected accounts pin to the top of the full list (then A–Z), then we page.
  const selectedIdSet = useMemo(
    () =>
      new Set(
        selectedCompareAccountIds
          .map(id => String(id || "").trim())
          .filter(Boolean)
      ),
    [selectedCompareAccountIds]
  );

  const orderedAccounts = useMemo(() => {
    return [...allLoadedAccounts].sort((left, right) => {
      const leftId = String(left.account_id || "").trim();
      const rightId = String(right.account_id || "").trim();
      const leftSelected = selectedIdSet.has(leftId);
      const rightSelected = selectedIdSet.has(rightId);
      if (leftSelected !== rightSelected) {
        return leftSelected ? -1 : 1;
      }
      if (showUnavailableBriefingData) {
        const leftPending =
          left.has_payload === false || isPendingDigestId(left.digest_id);
        const rightPending =
          right.has_payload === false || isPendingDigestId(right.digest_id);
        if (leftPending !== rightPending) {
          return leftPending ? 1 : -1;
        }
      }
      return String(left.account_name || "").localeCompare(
        String(right.account_name || ""),
        undefined,
        { sensitivity: "base" }
      );
    });
  }, [allLoadedAccounts, showUnavailableBriefingData, selectedIdSet]);

  const totalPages = Math.max(
    1,
    Math.ceil((orderedAccounts.length || ebAccounts.length) / pageSize) || 1
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Selecting an off-page account (e.g. from the sidebar) should land on page 1
  // where pinned selected rows are shown.
  const selectedIdsKey = selectedCompareAccountIds.join(",");
  useEffect(() => {
    if (!selectedCompareAccountIds.length) return;
    setPage(1);
  }, [selectedIdsKey]);

  const visibleAccounts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return orderedAccounts.slice(start, start + pageSize);
  }, [orderedAccounts, page, pageSize]);

  useEffect(() => {
    if (selectedCompareAccountIds.length === 0) {
      setCompareSelectionMessage(null);
      return;
    }
    if (selectedCompareAccountIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage("Select at least 2 accounts to compare.");
      return;
    }
    setCompareSelectionMessage(null);
  }, [selectedCompareAccountIds.length]);

  // Drop KPI cache when the visible account set changes.
  useEffect(() => {
    kpiLoadedForKeyRef.current = "";
    setKpiMatrixData([]);
    setKpiCategories([]);
  }, [accountIdsKey]);

  const accountIndex = section
    ? visibleAccounts.findIndex(a => a.digest_id === section.digestId)
    : -1;
  const account = accountIndex >= 0 ? visibleAccounts[accountIndex] : null;

  const openIfReady = (row: XFactorPortfolioAccount | null) => {
    if (!row || isPendingDigestId(row.digest_id)) return;
    setCardAccount(row);
  };

  const openBriefingIfReady = (row: XFactorPortfolioAccount) => {
    if (isPendingDigestId(row.digest_id)) return;
    setBriefingAccount(row);
  };

  const toggleCompareSelection = useCallback(
    (accountId: string) => {
      if (!accountId) return;
      setSelectedCompareAccountIds(prev => {
        let nextIds = prev;
        if (prev.includes(accountId)) {
          if (isCompareModalOpen && prev.length <= MIN_COMPARE_ACCOUNTS) {
            setCompareSelectionMessage(
              "At least 2 accounts are required in comparison."
            );
            return prev;
          }
          nextIds = prev.filter(id => id !== accountId);
        } else {
          nextIds = [...prev, accountId];
        }
        onHighlightedAccountIdsChange?.(nextIds);
        return nextIds;
      });
    },
    [isCompareModalOpen, onHighlightedAccountIdsChange]
  );

  const _loadKpiMatrixForCompare = useCallback(async (): Promise<{
    matrix: AccountMatrixData[];
    categories: string[];
  }> => {
    const ids = ebAccounts.map(a => a.id);
    if (!ids.length) return { matrix: [], categories: [] };

    if (
      kpiLoadedForKeyRef.current === accountIdsKey &&
      kpiMatrixData.length > 0
    ) {
      return { matrix: kpiMatrixData, categories: kpiCategories };
    }

    try {
      if (!kpiConfigRef.current) {
        try {
          kpiConfigRef.current = await loadMetricsConfigurationFromApi();
        } catch {
          kpiConfigRef.current = getDefaultMetricsConfiguration();
        }
      }
      const configuration = kpiConfigRef.current;
      const digestState = await fetchHawkeyeDigestState(ids, null, null, false);
      const rowAccounts = ebAccounts.map(a => ({
        id: a.id,
        name: a.name || "Unknown account",
      }));
      const matrix = buildAccountMatrixData(
        rowAccounts,
        configuration,
        digestState
      );
      const available = getAvailableCategories(digestState.digestAiScores);
      const categories = getOrderedCategories(configuration, available);
      kpiLoadedForKeyRef.current = accountIdsKey;
      setKpiMatrixData(matrix);
      setKpiCategories(categories);
      return { matrix, categories };
    } finally {
      /* loading tracked via kpiLoadedForKeyRef */
    }
  }, [accountIdsKey, ebAccounts, kpiCategories, kpiMatrixData]);

  const selectedCompareAccountsForStrip = useMemo(() => {
    const byId = new Map(
      visibleAccounts.map(a => [String(a.account_id || "").trim(), a])
    );
    const kpiById = new Map(kpiMatrixData.map(a => [a.account_id, a]));
    return selectedCompareAccountIds
      .map(id => {
        const kpi = kpiById.get(id);
        if (kpi) {
          return {
            account_id: kpi.account_id,
            account_name: kpi.account_name,
            overallScore: kpi.overallScore,
          };
        }
        const row = byId.get(id);
        const fromEb = ebAccounts.find(a => a.id === id);
        return {
          account_id: id,
          account_name: row?.account_name || fromEb?.name || "Unknown account",
          overallScore: row?.scores?.overall ?? undefined,
        };
      })
      .filter(a => a.account_id);
  }, [selectedCompareAccountIds, visibleAccounts, kpiMatrixData, ebAccounts]);

  const _handleCompareClick = async () => {
    if (selectedCompareAccountIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage("Select at least 2 accounts to compare.");
      return;
    }
    const { matrix } = await _loadKpiMatrixForCompare();
    const selected = matrix.filter(a =>
      selectedCompareAccountIds.includes(a.account_id)
    );
    const selectedIdsInMatrix = new Set(selected.map(a => a.account_id));
    const missingIds = selectedCompareAccountIds.filter(
      id => !selectedIdsInMatrix.has(id)
    );
    const withoutData = [
      ...getSelectedAccountsWithoutKpiData(selected),
      ...missingIds.map(id => {
        const fromEb = ebAccounts.find(a => a.id === id);
        const fromVisible = visibleAccounts.find(
          a => String(a.account_id || "").trim() === id
        );
        return {
          account_id: id,
          account_name:
            fromVisible?.account_name || fromEb?.name || "Unknown account",
          hasData: false as const,
        };
      }),
    ];
    if (withoutData.length > 0) {
      setIsMissingKpiDataModalOpen(true);
      return;
    }
    setIsCompareModalOpen(true);
  };

  const handleRemoveAccountsWithoutDataAndCompare = async () => {
    const { matrix } = await _loadKpiMatrixForCompare();
    const selected = matrix.filter(a =>
      selectedCompareAccountIds.includes(a.account_id)
    );
    const nextIds = selected
      .filter(account => account.hasData !== false)
      .map(account => String(account.account_id));

    setIsMissingKpiDataModalOpen(false);
    setSelectedCompareAccountIds(nextIds);
    onHighlightedAccountIdsChange?.(nextIds);

    if (nextIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage(
        `At least ${MIN_COMPARE_ACCOUNTS} accounts with KPIs data are required to compare.`
      );
      return;
    }

    setIsCompareModalOpen(true);
  };

  const selectedWithoutKpiForModal = useMemo(() => {
    const selected = kpiMatrixData.filter(a =>
      selectedCompareAccountIds.includes(a.account_id)
    );
    const without = getSelectedAccountsWithoutKpiData(selected);
    const inMatrix = new Set(selected.map(a => a.account_id));
    const missing = selectedCompareAccountIds
      .filter(id => !inMatrix.has(id))
      .map(id => {
        const fromEb = ebAccounts.find(a => a.id === id);
        const fromVisible = visibleAccounts.find(
          a => String(a.account_id || "").trim() === id
        );
        return {
          account_id: id,
          account_name:
            fromVisible?.account_name || fromEb?.name || "Unknown account",
          hasData: false as const,
        };
      });
    return [...without, ...missing];
  }, [kpiMatrixData, selectedCompareAccountIds, ebAccounts, visibleAccounts]);

  const spiderAllAccounts = useMemo(() => {
    if (kpiMatrixData.length) return kpiMatrixData;
    return ebAccounts.map(a => ({
      account_id: a.id,
      account_name: a.name || "Unknown account",
      overallScore: undefined,
      categoryScores: [] as { category: string; score: number }[],
      hasData: false,
    }));
  }, [kpiMatrixData, ebAccounts]);

  // noop — Compare is hidden on Business Health but the strip prop is required
  const noopCompare = useCallback(() => undefined, []);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 space-y-3 px-4 pt-4">
        {!loginOrgId ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            No organization on the current login session.
          </div>
        ) : null}

        {accountBriefings.error ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {accountBriefings.error}
          </div>
        ) : null}

        {!showUnavailableBriefingData &&
        pendingBriefingAccountIds.length > 0 ? (
          <p className="m-0 text-xs italic font-normal text-orange-500">
            Briefing data is currently not available for{" "}
            {pendingBriefingAccountIds.length} account
            {pendingBriefingAccountIds.length === 1 ? "" : "s"}.
          </p>
        ) : null}
      </div>

      <div
        ref={setMatrixScrollRef}
        className="scrollbar-hover relative min-h-0 flex-1 overflow-auto overscroll-contain"
      >
        {!ebAccounts.length ? (
          <p className="px-4 py-10 text-center text-sm text-gray-400">
            Select accounts to load Business Health briefings.
          </p>
        ) : (
          <>
            {!accountBriefings.loading && !allLoadedAccounts.length ? (
              <p className="px-4 py-10 text-center text-sm text-gray-400">
                No accounts in this view.
              </p>
            ) : (
              /*
                Matrix is the horizontal sticky scrollport and must be full-bleed
                (no horizontal padding on this ancestor).
              */
              <div
                className={
                  selectedCompareAccountIds.length > 0 ? "pb-20" : "pb-4"
                }
              >
                <BubbleMatrix
                  accounts={visibleAccounts}
                  payloads={payloads}
                  loading={
                    accountBriefings.loading && !allLoadedAccounts.length
                  }
                  onOpenBubble={(digestId, sectionId) => {
                    if (isPendingDigestId(digestId)) return;
                    setSection({ digestId, sectionId });
                  }}
                  showMetrics={false}
                  showLegend={false}
                  showAccountMeta={false}
                  showSummary
                  onOpenAccount={row => openIfReady(row)}
                  onOpenFullBriefing={openBriefingIfReady}
                  selectedAccountIds={selectedCompareAccountIds}
                  onToggleAccountSelection={toggleCompareSelection}
                  enableRowReveal={!hasRevealedOnce}
                  onRevealComplete={markRevealDone}
                  groupGeneratingLast={showUnavailableBriefingData}
                />
              </div>
            )}
          </>
        )}
      </div>

      {ebAccounts.length > 0 ? (
        <div className="shrink-0 px-4 pb-2 pt-1">
          <Pagination
            mode="dynamic"
            currentPage={page}
            totalPages={totalPages}
            totalItems={orderedAccounts.length || ebAccounts.length}
            pageSize={pageSize}
            hasPrevious={page > 1}
            hasNext={page < totalPages}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={BRIEFING_PAGE_SIZE_OPTIONS}
          />
        </div>
      ) : null}

      {selectedCompareAccountIds.length > 0 &&
      !isCompareModalOpen &&
      cardAccount == null ? (
        <KpiCompareSelectionStrip
          placement="overlay"
          panelRef={matrixScrollRef}
          selectedAccounts={selectedCompareAccountsForStrip}
          minCompareAccounts={MIN_COMPARE_ACCOUNTS}
          canCompare={false}
          showSpiderViewHint={false}
          hideCompareButton
          onCompare={noopCompare}
          onRemoveAccount={toggleCompareSelection}
          onClear={() => {
            setSelectedCompareAccountIds([]);
            onHighlightedAccountIdsChange?.([]);
          }}
          onSaveAsWatchlist={
            onSaveAsWatchlist
              ? () => onSaveAsWatchlist(selectedCompareAccountIds)
              : undefined
          }
        />
      ) : null}

      <Dialog open={isCompareModalOpen} onOpenChange={setIsCompareModalOpen}>
        <DialogContent
          hideClose
          className="flex max-h-[92vh] w-[95vw] max-w-[980px] flex-col overflow-hidden rounded-[14px] border border-slate-200 bg-white p-0 shadow-2xl"
        >
          <SpiderCompareModalContent
            allAccounts={spiderAllAccounts}
            selectedAccountIds={selectedCompareAccountIds}
            categoryOrder={kpiCategories}
            compareColors={COMPARE_COLORS}
            onToggleAccount={toggleCompareSelection}
            onClose={() => setIsCompareModalOpen(false)}
            validationMessage={compareSelectionMessage}
          />
        </DialogContent>
      </Dialog>

      <KpiCompareMissingDataModal
        isOpen={isMissingKpiDataModalOpen}
        onClose={() => setIsMissingKpiDataModalOpen(false)}
        onRemoveAndCompare={() => {
          void handleRemoveAccountsWithoutDataAndCompare();
        }}
        accountNames={selectedWithoutKpiForModal.map(a => a.account_name)}
        allSelectedLackData={
          selectedCompareAccountIds.length > 0 &&
          selectedWithoutKpiForModal.length === selectedCompareAccountIds.length
        }
      />

      <Modal
        open={cardAccount != null}
        onClose={() => {
          setCardAccount(null);
          setCardView("situation");
        }}
        width="max-w-5xl"
        title={
          <span>
            {cardView === "situation" ? "Situation Card" : "Executive Brief"} —{" "}
            <span style={{ color: ACCENT }}>
              {cardAccount?.account_name || "Unknown account"}
            </span>
          </span>
        }
        subtitle={
          cardAccount
            ? [
                `Public ${formatDate(cardAccount.digest_date) || "—"}`,
                cardAccount.is_stale ? "STALE" : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined
        }
        actions={
          <button
            type="button"
            onClick={() =>
              setCardView(v => (v === "situation" ? "brief" : "situation"))
            }
            className="inline-flex shrink-0 items-center gap-1.5 self-center whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#ad46ff" }}
          >
            {cardView === "situation" ? (
              <>
                Executive Brief <ArrowRight size={13} aria-hidden />
              </>
            ) : (
              <>
                <ArrowLeft size={13} aria-hidden /> Situation Card
              </>
            )}
          </button>
        }
        subheader={
          cardView === "brief" ? <ExecutiveBriefSectionNav /> : undefined
        }
      >
        {cardAccount ? (
          cardView === "situation" ? (
            <SituationCard
              accountName={cardAccount.account_name || "Unknown account"}
              data={payloads[cardAccount.digest_id]?.data?.data}
            />
          ) : (
            <ExecutiveBrief
              accountName={cardAccount.account_name || "Unknown account"}
              data={payloads[cardAccount.digest_id]?.data?.data}
              hideSectionNav
            />
          )
        ) : null}
      </Modal>

      <FullBriefingModal
        open={briefingAccount != null}
        onClose={() => setBriefingAccount(null)}
        accountName={briefingAccount?.account_name || "Unknown account"}
        digestDate={briefingAccount?.digest_date}
        data={payloads[briefingAccount?.digest_id || ""]?.data?.data}
      />

      <SectionDetailModal
        open={section != null && !isPendingDigestId(section?.digestId)}
        onClose={() => setSection(null)}
        accountName={account?.account_name || "Unknown account"}
        digestDate={account?.digest_date}
        data={payloads[section?.digestId || ""]?.data?.data}
        sectionId={section?.sectionId || "summary"}
        onNavigate={sectionId => setSection(s => (s ? { ...s, sectionId } : s))}
        accountIndex={accountIndex}
        accountTotal={visibleAccounts.length}
        onStepAccount={delta => {
          let i = accountIndex + delta;
          while (i >= 0 && i < visibleAccounts.length) {
            const target = visibleAccounts[i];
            if (target && !isPendingDigestId(target.digest_id)) {
              setSection(s => (s ? { ...s, digestId: target.digest_id } : s));
              return;
            }
            i += delta;
          }
        }}
      />
    </div>
  );
};

export default TempPublicDataPanel;
